import { Search, X } from "lucide-react";
import { ApiQuery, SelectOptionValue } from "~/modules/filters/types";
import { AssetColumns } from "~/modules/assets/types";
import {
    FilterFormField,
    FilterFormReset,
} from "~/modules/filters/components/FilterFormContext";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { MultiSelect } from "~/components/MultiSelect";
import { cn } from "~/lib/utils";
import { Checkbox } from "~/components/ui/checkbox";
import { ASSET_CLASS_IDS } from "~/consts";
import { SEARCH_FILTER_METADATA } from "~/modules/filters/consts";
import { FilterLabel } from "../FilterLabel";
import { NON_KYC_FILTER_METADATA } from "../../consts";
import {
    trackFilterAssetClass,
    trackFilterClearAll,
    trackFilterNetwork,
    trackFilterNonKyc,
    trackFilterRedemption,
    trackFilterSearch,
    trackFilterYieldRange,
} from "../../analytics";
import { YieldRangeFilter } from "./YieldRangeFilter";
import { AssetClassFilter } from "./AssetClassFilter";
import {
    getApyRange,
    getApyRangeMax,
    getFilterValue,
    getMultiSelectOptions,
    getMultiSelectValue,
    getRedemptionFrequencyFilterValue,
    getRedemptionTermMultiSelectOptions,
    getSelectedRedemptionTermOptions,
    getTopLevelFilters,
    setApyRange,
} from "./functions";
import {
    FILTER_CONTROL_BASE_CLASS,
    FILTER_CONTROL_INTERACTIVE_CLASS,
} from "./styles";

// // // //

// Asset classes the base filter always excludes - no point offering them
const EXCLUDED_ASSET_CLASS_IDS = new Set<SelectOptionValue>([
    ASSET_CLASS_IDS.Cryptocurrency,
    ASSET_CLASS_IDS.Stablecoin,
]);

// // // //
// 30D APY range
// FilterFormContext keeps one filter per field, so a min + max on apy_30_day
// can't be expressed through FilterFormField - instead the range lives in the
// query as a nested `and` group: { and: [apy_30_day gte min, apy_30_day lte max] }.
// FilterFormContext leaves nested groups untouched, and resetFilters clears it.

const APY_RANGE_MIN = 0;

// // // //

// Transferability types with a no-KYC purchase path
// (2 = without restrictions, 4 = with blacklist)
const NON_KYC_TRANSFERABILITY_TYPE_IDS = [2, 4];

const SEARCH_DEBOUNCE_MS = 300;

/**
 * AssetScreenerFilters
 * Asset screener filter panel - asset class categories/classes, search, 30D
 * APY range, redemption terms, networks, and a Non-KYC toggle, plus a "Clear
 * filters" action. Must be rendered inside a FilterFormContextProvider; the
 * APY range is written to `apiQuery` directly via `onChangeQuery`.
 */
export function AssetScreenerFilters(props: {
    apiQuery: ApiQuery;
    onChangeQuery: (updatedQuery: ApiQuery) => void;
    apy30DayValues: number[];
    assetClassCounts: Record<string, number>;
    networkCounts: Record<string, number>;
    redemptionFrequencyCounts: Record<string, number>;
}) {
    const {
        apiQuery,
        onChangeQuery,
        apy30DayValues,
        assetClassCounts,
        networkCounts,
        redemptionFrequencyCounts,
    } = props;

    const apyRangeMax = getApyRangeMax(apy30DayValues);

    const activeFilterCount = getTopLevelFilters(apiQuery).length;

    return (
        <div className="space-y-4 text-sm select-none">
            {/* Row 1: Asset Class filter */}
            <FilterFormField column={AssetColumns.assetClassID}>
                {({ filterMetadata, value, onChange }) => (
                    <AssetClassFilter
                        selectedAssetClasses={getMultiSelectValue(value)}
                        onAssetClassesChange={(selected) => {
                            trackFilterAssetClass(selected);
                            onChange(getFilterValue(filterMetadata, selected));
                        }}
                        assetClassOptions={getMultiSelectOptions(
                            {
                                ...filterMetadata,
                                selectOptions:
                                    filterMetadata.selectOptions?.filter(
                                        (option) =>
                                            !EXCLUDED_ASSET_CLASS_IDS.has(
                                                option.value,
                                            ),
                                    ),
                            },
                            assetClassCounts,
                        )}
                    />
                )}
            </FilterFormField>

            {/* Row 2: Search + additional filters */}
            <div className="space-y-4">
                <div className="space-y-1">
                    <FilterLabel label="Filters" />
                    <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_max-content] lg:gap-4">
                        <FilterFormField
                            column={SEARCH_FILTER_METADATA.field}
                            debounce={SEARCH_DEBOUNCE_MS}
                        >
                            {({ value, onChange }) => (
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        className={cn(
                                            FILTER_CONTROL_BASE_CLASS,
                                            "h-9 border-border bg-card pl-9 focus-visible:ring-1 focus-visible:ring-blue-300",
                                        )}
                                        placeholder="Search any asset"
                                        value={
                                            typeof value === "string"
                                                ? value
                                                : ""
                                        }
                                        onChange={(e) => {
                                            trackFilterSearch(e.target.value);
                                            onChange(e.target.value);
                                        }}
                                    />
                                </div>
                            )}
                        </FilterFormField>

                        <div>
                            <YieldRangeFilter
                                value={getApyRange(apiQuery, apyRangeMax)}
                                onValueChange={(range) =>
                                    onChangeQuery(
                                        setApyRange(
                                            apiQuery,
                                            range,
                                            apyRangeMax,
                                        ),
                                    )
                                }
                                onApply={trackFilterYieldRange}
                                data={apy30DayValues}
                                min={APY_RANGE_MIN}
                                max={apyRangeMax}
                            />
                        </div>

                        <FilterFormField
                            column={
                                AssetColumns.primaryMarketRedemptionFrequency
                            }
                        >
                            {({ value, onChange }) => (
                                <div>
                                    <MultiSelect
                                        options={getRedemptionTermMultiSelectOptions(
                                            redemptionFrequencyCounts,
                                        )}
                                        value={getSelectedRedemptionTermOptions(
                                            value,
                                        )}
                                        onChange={(selected) => {
                                            trackFilterRedemption(selected);
                                            onChange(
                                                getRedemptionFrequencyFilterValue(
                                                    selected,
                                                ),
                                            );
                                        }}
                                        placeholder="All redemption terms"
                                        className={`${FILTER_CONTROL_INTERACTIVE_CLASS} w-full`}
                                        maxBadgesShown={3}
                                    />
                                </div>
                            )}
                        </FilterFormField>

                        <FilterFormField column={AssetColumns.networkIDs}>
                            {({ filterMetadata, value, onChange }) => (
                                <div>
                                    <MultiSelect
                                        options={getMultiSelectOptions(
                                            filterMetadata,
                                            networkCounts,
                                        )}
                                        value={getMultiSelectValue(value)}
                                        onChange={(selected) => {
                                            trackFilterNetwork(selected);
                                            onChange(
                                                getFilterValue(
                                                    filterMetadata,
                                                    selected,
                                                ),
                                            );
                                        }}
                                        placeholder="All networks"
                                        className={`${FILTER_CONTROL_INTERACTIVE_CLASS} w-full`}
                                        maxBadgesShown={1}
                                        searchEnabled
                                        searchPlaceholder="Search networks..."
                                    />
                                </div>
                            )}
                        </FilterFormField>

                        <FilterFormField column={NON_KYC_FILTER_METADATA.field}>
                            {({ value, onChange }) => (
                                <label
                                    className={cn(
                                        FILTER_CONTROL_INTERACTIVE_CLASS,
                                        "flex h-9 w-fit cursor-pointer items-center gap-2 whitespace-nowrap px-3",
                                    )}
                                >
                                    <Checkbox
                                        checked={
                                            Array.isArray(value) &&
                                            value.length > 0
                                        }
                                        onCheckedChange={(checked) => {
                                            trackFilterNonKyc(checked === true);
                                            // Empty array clears the filter
                                            onChange(
                                                checked === true
                                                    ? NON_KYC_TRANSFERABILITY_TYPE_IDS
                                                    : [],
                                            );
                                        }}
                                    />
                                    Non-KYC only
                                </label>
                            )}
                        </FilterFormField>
                    </div>
                </div>

                {/* Row 3: Clear filters */}
                {activeFilterCount > 0 && (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                        <FilterFormReset>
                            {({ resetFilters }) => (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-9 shrink-0 cursor-pointer gap-1 px-2 text-sm font-medium text-blue-600 shadow-none hover:bg-transparent hover:text-blue-700"
                                    onClick={() => {
                                        trackFilterClearAll(activeFilterCount);
                                        resetFilters();
                                    }}
                                >
                                    <X className="h-4 w-4 shrink-0" />
                                    Clear {activeFilterCount} filter
                                    {activeFilterCount > 1 ? "s" : ""}
                                </Button>
                            )}
                        </FilterFormReset>
                    </div>
                )}
            </div>
        </div>
    );
}
